import { Configuration, PublicClientApplication, LogLevel } from '@azure/msal-browser';

// Get runtime config from window.ENV (set by config.js in index.html)
const env = (window as any).ENV || {};

// Hardcoded fallback client ID to prevent AADSTS900144 errors when config fails to load
const FALLBACK_CLIENT_ID = 'd91c0159-73f4-4a91-81be-1ea9010401d1';

export const msalConfig: Configuration = {
  auth: {
    clientId: env.VITE_AZURE_CLIENT_ID || FALLBACK_CLIENT_ID,
    authority: 'https://login.microsoftonline.com/common',
    redirectUri: env.VITE_REDIRECT_URI || window.location.origin,
    postLogoutRedirectUri: window.location.origin,
    navigateToLoginRequestUrl: true,
  },
  cache: {
    cacheLocation: 'localStorage',
    storeAuthStateInCookie: true,
    secureCookies: true,
  },
  system: {
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) return;
        switch (level) {
          case LogLevel.Error:
            console.error(message);
            break;
          case LogLevel.Warning:
            console.warn(message);
            break;
          default:
            if (import.meta.env.DEV) {
              console.log(message);
            }
        }
      },
      logLevel: LogLevel.Warning,
    },
  },
};

export const msalInstance = new PublicClientApplication(msalConfig);

export const loginRequest = {
  scopes: ['User.Read', 'email', 'profile'],
  prompt: 'select_account' as const,
};

// Track initialization state
let isInitialized = false;
let initializationPromise: Promise<void> | null = null;

// Initialize MSAL
export const initializeMsal = async () => {
  // If already initialized, return immediately
  if (isInitialized) {
    return;
  }

  // If initialization is in progress, wait for it
  if (initializationPromise) {
    return initializationPromise;
  }

  // Start initialization
  initializationPromise = (async () => {
    try {
      await msalInstance.initialize();

    // Handle redirect promise first - this must complete before any other MSAL operations
    // This is critical to prevent interaction_in_progress errors
    const response = await msalInstance.handleRedirectPromise();
    if (response) {
      msalInstance.setActiveAccount(response.account);
    }

      // After handling redirect, set active account if available
      const accounts = msalInstance.getAllAccounts();
      if (accounts.length > 0 && !msalInstance.getActiveAccount()) {
        msalInstance.setActiveAccount(accounts[0]);
      }

      // Mark as initialized
      isInitialized = true;
    } catch (error) {
      console.error('Error initializing MSAL:', error);
      // Clear all MSAL interaction flags on error
      Object.keys(localStorage).forEach(key => {
        if (key.includes('interaction.status')) {
          localStorage.removeItem(key);
        }
      });
      // Reset initialization tracking on error so it can be retried
      initializationPromise = null;
      throw error;
    }
  })();

  return initializationPromise;
};
